<template>
  <div class="page-wrap">

    <!-- ── Premium Table ─────────────────────── -->
    <PremiumTable
      title="Blocks Management"
      subtitle="Manage society building blocks or wings"
      add-label="Add"
      :show-edit="true"
      :show-delete="true"
      @add="openAddDialog"
      @edit="openEditDialog"
      @delete="confirmDeleteDialog($event.id)"
      :columns="columns"
      :items="flats"
      :loading="loading"
      :total-items="flats.length"
      :page.sync="page"
      :items-per-page.sync="itemsPerPage"
      :searchable="true"
      :search="search"
      @search="onSearch"
    >
      <!-- Custom Cells -->
      <template #col-name="{ item }">
        <div class="block-cell">
          <v-icon size="18" color="#3B82F6" class="mr-3">mdi-office-building-outline</v-icon>
          <span class="block-name-text">{{ item.name }}</span>
        </div>
      </template>
    </PremiumTable>

    <!-- ── Add / Edit Modal ────────────────── -->
    <PremiumModal
      v-model="addFlatDialog"
      :title="(flat.id ? 'Edit' : 'New') + ' Block'"
      :loading="saveLoading"
      :submit-text="'Submit'"
      max-width="440"
      @submit="flat.id ? updateFlat(flat.id) : saveFlat()"
      @close="closeDialog"
    >
      <PremiumInput
        v-model="flat.name"
        label="Block Name"
        placeholder="e.g. Block A, Wing B, etc."
        icon="mdi-office-building-outline"
        v-validate="'required'"
        name="name"
        data-vv-as="Block Name"
        :error="errors.first('name') || serverSideErrors.name"
      />
    </PremiumModal>

    <!-- ── Confirm Deletion ─────────────────── -->
    <ConfirmModal
      v-model="deleteDialog"
      type="danger"
      title="Delete Block"
      message="Are you sure you want to delete this block?"
      confirm-text="Yes, Delete"
      cancel-text="Cancel"
      :loading="deleteLoading"
      @confirm="deleteFlat"
    />

  </div>
</template>

<script>
import { mapMutations, mapGetters, mapState } from "vuex";
import PremiumTable from "../../common/PremiumTable.vue";
import PremiumModal from "../../common/PremiumModal.vue";
import PremiumInput from "../../common/PremiumInput.vue";
import ConfirmModal from "../../common/ConfirmModal.vue";
import filterMixin from "../../../mixins/filterMixin";

export default {
  name: 'BlockList',
  mixins: [filterMixin],
  components: { PremiumTable, PremiumModal, PremiumInput, ConfirmModal },

  data() {
    return {
      search:           "",
      flats:            [],
      loading:          false,
      saveLoading:      false,
      deleteLoading:    false,
      addFlatDialog:    false,
      deleteDialog:     false,
      delete_id:        "",
      page:             1,
      itemsPerPage:     10,
      flat:             { id: null, name: "" },
      serverSideErrors: {},
      user:             {},
    };
  },

  computed: {
    ...mapGetters("society", ["useBlocks"]),
    ...mapState('theme', ['primaryColor']),

    columns() {
      return [
        { key: 'name', label: this.$t("blocks.headers.block_name") || 'Block Name' },
      ];
    },
  },

  watch: {
    useBlocks(newVal) {
      if (!newVal) this.$router.replace({ path: "/societyadmin/dashboard" });
    },
    search() {
      this.page = 1;
      this.getFlats();
    },
  },

  methods: {
    ...mapMutations("alert", ["setAlert"]),

    onSearch(val) {
      this.search = val;
    },

    getFlats() {
      this.loading = true;
      this.$axios.get("societyadmin/flats", {
        params: {
          search: this.search || ""
        }
      }).then(response => {
        this.loading = false;
        if (response.data.status) {
          this.flats = response.data.flats;
        } else {
          this.showError(response.data.message);
          if (response.data.type === 'invalid') {
            localStorage.removeItem("user");
            this.$router.push("/societyadmin/login");
          }
        }
      }).catch(() => { this.loading = false; });
    },

    saveFlat() {
      this.$validator.validateAll().then(success => {
        if (success) {
          this.saveLoading = true;
          this.flat.society_id = this.user.id;
          this.$axios.post("/societyadmin/flats", this.flat).then(response => {
            this.saveLoading = false;
            if (response.data.status) {
              this.getFlats();
              this.closeDialog();
            } else {
              this.handleErrors(response.data);
            }
          }).catch(() => { this.saveLoading = false; });
        }
      });
    },

    updateFlat(id) {
      this.$validator.validateAll().then(success => {
        if (success) {
          this.saveLoading = true;
          this.flat.society_id = this.user.id;
          this.$axios.put("/societyadmin/flats/" + id, this.flat).then(response => {
            this.saveLoading = false;
            if (response.data.status) {
              this.getFlats();
              this.closeDialog();
            } else {
              this.handleErrors(response.data);
            }
          }).catch(() => { this.saveLoading = false; });
        }
      });
    },

    deleteFlat() {
      this.deleteLoading = true;
      this.$axios.delete("societyadmin/flats/" + this.delete_id).then(response => {
        this.deleteLoading = false;
        if (response.data.status) {
          this.deleteDialog = false;
          this.getFlats();
          this.setAlert({ showAlert: true, variant: "green accent-4", timeout: "2000", message: response.data.message });
        } else {
          this.showError(response.data.message);
        }
      }).catch(() => { this.deleteLoading = false; });
    },

    openAddDialog() {
      this.flat = { id: null, name: "" };
      this.$validator.reset();
      this.serverSideErrors = {};
      this.addFlatDialog = true;
    },

    openEditDialog(item) {
      this.flat = { ...item };
      this.$validator.reset();
      this.serverSideErrors = {};
      this.addFlatDialog = true;
    },

    confirmDeleteDialog(id) {
      this.delete_id = id;
      this.deleteDialog = true;
    },

    closeDialog() {
      this.addFlatDialog = false;
      this.flat = { id: null, name: "" };
      this.$validator.reset();
      this.serverSideErrors = {};
    },

    handleErrors(data) {
      if (data.errors) {
        this.serverSideErrors = {};
        data.errors.forEach(e => { this.serverSideErrors[e.field] = e.message; });
      } else {
        this.showError(data.message);
      }
    },

    showError(msg) {
      this.setAlert({ showAlert: true, variant: "red accent-3", timeout: "2000", message: msg });
    }
  },

  mounted() {
    this.user   = localStorage.getItem("user") ? JSON.parse(localStorage.getItem("user")) : {};
    
    // Initialize persistent filters
    this.initFilters('block');

    if (!this.useBlocks) {
      this.$router.replace({ path: "/societyadmin/dashboard" });
      return;
    }

    this.getFlats();
  },
};
</script>

<style scoped>
.page-wrap {
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
  max-width: 100%;
  overflow-x: hidden;
  box-sizing: border-box;
}

.block-cell {
  display: flex;
  align-items: center;
  min-width: 0;
}
.block-name-text {
  font-size: 14px;
  font-weight: 700;
  color: var(--tx);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>